import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Bell,
  BarChart3,
  CircleHelp,
  Copy as CopyIcon,
  Home,
  LayoutDashboard,
  Lock,
  LogOut,
  Menu as MenuIcon,
  Settings,
  UserRound,
  X,
} from 'lucide-react';
import { Dialog, Popover } from 'radix-ui';
import { Suspense, useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { BrandIcon } from '../components/brand.js';
import { ColorModeMenu } from '../components/color-mode.js';
import {
  Button,
  buttonClass,
  cn,
  ErrorBanner,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Spinner,
} from '../components/ui.js';
import { formatDateTime, formatLongDate } from '../lib/format.js';
import { GROUP_LABELS, hasDashboard, navEntities, useAppMeta, type AppMeta } from '../lib/meta.js';
import { useExtension, useSlot } from '../lib/slots.js';
import { applyBranding } from '../lib/theme.js';
import { errorMessage, trpc, trpcClient } from '../lib/trpc.js';
import { WhatsNewLink } from '../help/whats-new.js';
import { SampleBadge } from './owner-dashboard.js';

const navLink =
  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900';
const navActive =
  'bg-brand-soft font-medium text-brand-strong hover:bg-brand-soft hover:text-brand-strong';

function NavGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mt-5">
      <p className="mb-1 px-2.5 text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
        {label}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

export function Shell() {
  const meta = useAppMeta();
  const extension = useExtension();
  useEffect(() => applyBranding(meta.branding), [meta.branding]);
  const mismatch = extension && meta.client && extension.client !== meta.client.slug;
  const admin = meta.capabilities.length > 0;

  return (
    <div className="flex h-full">
      {/* Beside the page on a wide screen; behind the menu button on a phone or tablet. */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-slate-200 bg-surface lg:flex">
        <NavPanel />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-1 border-b border-slate-200 bg-surface px-2 sm:gap-2 sm:px-6">
          <NavDrawer />
          <BrandIcon
            name={meta.branding.product_name}
            className="mr-1 size-7 shrink-0 lg:hidden"
            tile="rounded-md text-xs"
          />
          <div className="min-w-0 flex-1">
            <SampleBadge />
          </div>
          <Notifications />
          <Menu>
            <MenuTrigger className={buttonClass('ghost')} aria-label="Account">
              <UserRound className="size-4" aria-hidden />
              <span className="hidden sm:inline">{meta.user.name}</span>
            </MenuTrigger>
            <MenuContent>
              <MenuLabel>
                {meta.user.email}
                <br />
                {meta.user.roles.join(', ')}
              </MenuLabel>
              <MenuSeparator />
              <ColorModeMenu />
              <MenuSeparator />
              <SettingsItem admin={admin} />
              <SignOutItem />
            </MenuContent>
          </Menu>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-6">
            <CopyNotice meta={meta} />
            <AccessNotice meta={meta} />
            {mismatch && (
              <ErrorBanner title="This build carries another client's screens">
                It was built for {extension.client}, but this deploy is {meta.client?.slug}. Rebuild
                the web app with DATABINK_CLIENT={meta.client?.slug}.
              </ErrorBanner>
            )}
            <Suspense fallback={<Spinner />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}

/** From this width the menu sits beside the page (Tailwind's `lg`), and the drawer is not used. */
const WIDE = '(min-width: 64rem)';

/**
 * The menu on a phone or tablet: a button in the header that slides the same panel the wide
 * screen shows beside the page in from the left. Choosing a page closes it, and so does the
 * screen growing wide enough for the menu beside the page (a tablet turned sideways), which
 * would otherwise leave an unseen drawer holding the page still.
 */
function NavDrawer() {
  const [open, setOpen] = useState(false);
  const href = useRouterState({ select: (state) => state.location.href });
  useEffect(() => setOpen(false), [href]);
  useEffect(() => {
    const wide = window.matchMedia(WIDE);
    const changed = () => {
      if (wide.matches) setOpen(false);
    };
    wide.addEventListener('change', changed);
    return () => wide.removeEventListener('change', changed);
  }, []);
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger className={cn(buttonClass('ghost'), 'px-2 lg:hidden')} aria-label="Menu">
        <MenuIcon className="size-5" aria-hidden />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-night/40 lg:hidden" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-surface shadow-xl motion-safe:animate-[drawer-in_160ms_ease-out] lg:hidden"
        >
          <Dialog.Title className="sr-only">Menu</Dialog.Title>
          <Dialog.Close
            className={cn(buttonClass('ghost', 'sm'), 'absolute top-4 right-3')}
            aria-label="Close menu"
          >
            <X className="size-4" aria-hidden />
          </Dialog.Close>
          <NavPanel onNavigate={() => setOpen(false)} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * The product, the organization and the pages: beside the page, or in the drawer, which
 * `onNavigate` closes when a page is chosen, the one already open included.
 */
function NavPanel({ onNavigate }: { onNavigate?: () => void }) {
  const meta = useAppMeta();
  const navSlot = useSlot('app.nav');
  // A plain click opens the page here; one with a key held (a new tab) leaves the menu open.
  const chosen = (event: MouseEvent) => {
    if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey)
      onNavigate?.();
  };
  const groups = new Map<string, ReturnType<typeof navEntities>>();
  const dashboard = hasDashboard(meta);
  for (const entity of navEntities(meta)) {
    const group = entity.group ?? 'other';
    // Copies from the business's tools are listed once there are some, and only the kinds
    // their tools send.
    if (group === 'pulled' && !meta.dashboard.has_data) continue;
    if (meta.dashboard.without_data.includes(entity.id)) continue;
    groups.set(group, [...(groups.get(group) ?? []), entity]);
  }
  return (
    <>
      <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-4 pr-12 lg:pr-4">
        <BrandIcon name={meta.branding.product_name} className="size-8" tile="rounded-lg text-sm" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            {meta.branding.product_name}
          </p>
          <p className="truncate text-xs text-slate-500">{meta.organization.name}</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-6" aria-label="Main">
        <div className="mt-4 space-y-0.5">
          {dashboard ? (
            <Link
              to="/dashboard"
              className={navLink}
              onClick={chosen}
              activeProps={{ className: navActive }}
            >
              <LayoutDashboard className="size-4" aria-hidden /> Dashboard
            </Link>
          ) : (
            <Link
              to="/"
              className={navLink}
              onClick={chosen}
              activeProps={{ className: navActive }}
              activeOptions={{ exact: true }}
            >
              <Home className="size-4" aria-hidden /> Home
            </Link>
          )}
          {meta.reports.length > 0 && (
            <Link
              to="/reports"
              className={navLink}
              onClick={chosen}
              activeProps={{ className: navActive }}
            >
              <BarChart3 className="size-4" aria-hidden /> Reports
            </Link>
          )}
          <Link
            to="/help"
            className={navLink}
            onClick={chosen}
            activeProps={{ className: navActive }}
          >
            <CircleHelp className="size-4" aria-hidden /> Help
          </Link>
          <WhatsNewLink className={navLink} active={navActive} onClick={chosen} />
        </div>
        {[...groups.entries()].map(([group, entities]) => (
          <NavGroup key={group} label={GROUP_LABELS[group] ?? group}>
            {entities.map((e) => (
              <Link
                key={e.id}
                to="/e/$entity"
                params={{ entity: e.id }}
                className={navLink}
                onClick={chosen}
                activeProps={{ className: navActive }}
              >
                {e.plural}
              </Link>
            ))}
          </NavGroup>
        ))}
        {navSlot.length > 0 && (
          <NavGroup label={meta.client?.name ?? 'More'}>
            {navSlot.map((item) => (
              <a key={item.id} href={item.path} className={navLink} onClick={chosen}>
                {item.label}
              </a>
            ))}
          </NavGroup>
        )}
      </nav>
      <div className="border-t border-slate-100 px-4 py-3 text-[11px] text-slate-400">
        Core {meta.core_version}
        {meta.client && ` · ${meta.client.slug} ${meta.client.version}`}
      </div>
    </>
  );
}

/**
 * On every screen of a neutralized copy of a deploy's data (a restored backup, a release
 * rehearsal): red, so nobody mistakes it for the live system.
 */
function CopyNotice({ meta }: { meta: AppMeta }) {
  if (!meta.copy.neutralized) return null;
  return (
    <div
      role="status"
      className="mb-5 flex gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900"
    >
      <CopyIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p>
        <span className="font-medium">This is a copy of the data, not the live system.</span>{' '}
        Nothing leaves it: no email, webhooks, accounting updates or background jobs, and scheduled
        work does not run. Changes here never reach the live system.
        {meta.copy.since ? ` Copy made ${formatDateTime(meta.copy.since)}.` : ''}
      </p>
    </div>
  );
}

/** On every screen of a read-only deploy: why nothing can be changed, and what still works. */
function AccessNotice({ meta }: { meta: AppMeta }) {
  const { state, export_until: until } = meta.access;
  if (state === 'active') return null;
  const canExport = meta.capabilities.includes('export_data');
  return (
    <div
      role="status"
      className="mb-5 flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
    >
      <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
      {state === 'suspended' ? (
        <p>
          <span className="font-medium">This account is suspended.</span> It is read-only while it
          is suspended: signing in, looking things up and exporting your data still work.
        </p>
      ) : (
        <p>
          <span className="font-medium">This subscription has ended.</span> The account is
          read-only.{' '}
          {canExport ? (
            <>
              Export your data from{' '}
              <Link to="/settings" className="font-medium underline">
                Settings
              </Link>
            </>
          ) : (
            'Ask an administrator to export your data'
          )}
          {until ? ` before ${formatLongDate(until)}, when it is erased.` : '.'}
        </p>
      )}
    </div>
  );
}

function SettingsItem({ admin }: { admin: boolean }) {
  const navigate = useNavigate();
  return (
    <MenuItem onSelect={() => void navigate({ to: '/settings' })}>
      <Settings className="size-4" aria-hidden /> {admin ? 'Settings' : 'Your account'}
    </MenuItem>
  );
}

function SignOutItem() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: () => trpcClient.auth.logout.mutate(),
    onSettled: async () => {
      queryClient.clear();
      await navigate({ to: '/login' });
    },
  });
  return (
    <MenuItem onSelect={() => logout.mutate()}>
      <LogOut className="size-4" aria-hidden /> Sign out
    </MenuItem>
  );
}

function Notifications() {
  const queryClient = useQueryClient();
  const list = useQuery({ ...trpc.notifications.list.queryOptions(), refetchInterval: 60_000 });
  const unread = (list.data ?? []).filter((n) => !n.read_at).length;
  const markRead = useMutation({
    mutationFn: () => trpcClient.notifications.markRead.mutate({}),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: trpc.notifications.list.queryKey() }),
  });
  return (
    <Popover.Root>
      <Popover.Trigger
        className={cn(buttonClass('ghost'), 'relative')}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        <Bell className="size-4" aria-hidden />
        {unread > 0 && (
          <span className="absolute top-1 right-1.5 flex size-4 items-center justify-center rounded-full bg-accent text-[10px] font-semibold text-white">
            {unread}
          </span>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-50 w-80 rounded-md border border-slate-200 bg-surface shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <p className="text-sm font-semibold text-slate-800">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate()}
                className="text-xs text-brand-strong hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {(list.data ?? []).length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-slate-500">Nothing yet</li>
            )}
            {(list.data ?? []).map((n) => (
              <li
                key={n.id}
                className={cn(
                  'border-b border-slate-50 px-3 py-2.5 text-sm',
                  !n.read_at && 'bg-brand-soft/60',
                )}
              >
                {n.approval ? (
                  // A question for this person: it opens the record it is about.
                  <Link
                    to="/e/$entity/$id"
                    params={{ entity: n.approval.entity, id: n.approval.record_id }}
                    className="font-medium text-slate-800 hover:underline"
                  >
                    {n.title}
                  </Link>
                ) : n.entity && n.record_id ? (
                  <Link
                    to="/e/$entity/$id"
                    params={{ entity: n.entity, id: n.record_id }}
                    className="font-medium text-slate-800 hover:underline"
                  >
                    {n.title}
                  </Link>
                ) : (
                  <p className="font-medium text-slate-800">{n.title}</p>
                )}
                {n.body && <p className="text-slate-600">{n.body}</p>}
                {n.approval?.can_answer && n.record_id && <AnswerButtons id={n.record_id} />}
                <p className="mt-0.5 text-xs text-slate-400">{formatDateTime(n.created_at)}</p>
              </li>
            ))}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Approve or Deny, for a question an automation rule asked this person. */
function AnswerButtons({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const answer = useMutation({
    mutationFn: (choice: 'approve' | 'deny') =>
      trpcClient.approvals.answer.mutate({ id, answer: choice }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: trpc.notifications.list.queryKey() }),
  });
  return (
    <div className="mt-1.5">
      {answer.error && <p className="mb-1 text-xs text-danger">{errorMessage(answer.error)}</p>}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          busy={answer.isPending && answer.variables === 'approve'}
          disabled={answer.isPending}
          onClick={() => answer.mutate('approve')}
          data-testid={`approve-${id}`}
        >
          Approve
        </Button>
        <Button
          size="sm"
          busy={answer.isPending && answer.variables === 'deny'}
          disabled={answer.isPending}
          onClick={() => answer.mutate('deny')}
          data-testid={`deny-${id}`}
        >
          Deny
        </Button>
      </div>
    </div>
  );
}
